import cn from 'clsx'
import React, { useRef, useCallback, useMemo, useEffect, useState, useContext } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import { replace, RouterLocation } from 'connected-react-router'
import cx from 'clsx'

import OfferPageGallery from '../OfferPageGallery'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import OfferIntro from './UltraLuxIntro'
import { fetchAvailableRatesForOffer, fetchOfferFlightPrice } from 'actions/OfferActions'
import noop from 'lib/function/noop'
import config from 'constants/config'
import Breakpoint from 'components/Common/Breakpoint'
import FlashHotelHeader from '../FlashHotel/FlashHotelStickyHeader'
import OfferAirportSelect from '../Filters/Airport/OfferAirportSelect'
import OfferAirportSelectBox from '../Filters/Airport/OfferAirportSelectBox'
import LayoutContainer from 'components/Common/LayoutContainer'
import Backdrop from 'components/utils/Backdrop'
import DesktopRelatedTravelItems from 'components/OfferPage/RelatedTravelItems'
import FlashHotelSidebarFilters from '../FlashHotel/FlashHotelSidebarFilters'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import { OfferPageActions, OfferPageDispatchAction, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import useMountedEffect from 'hooks/useMountedEffect'
import OfferCalendarDatePickerSelectBox from '../Filters/CalendarPicker/OfferCalendarDatePickerSelectBox'
import OfferCalendarDatePickerSelect from '../Filters/CalendarPicker/OfferCalendarDatePickerSelect'
import { clearBookingCalendar, fetchCalendars, fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import usePrevious from 'hooks/usePrevious'
import { removeSearchParamsFromURL } from 'lib/search/searchUtils'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import { useSyncHotelTripItem } from 'tripPlanner/hooks/bookmarks/useSyncHotelTripItem'
import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import { urlHashDatePicker } from 'constants/url'
import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import { getInitialLeOfferPageState } from 'selectors/offerPage/offerPageSelectors'
import Heading from 'components/Luxkit/Typography/Heading'
import OfferUpgradeModalContext from '../OfferUpgradeModalContext'
import { generateRoomOccupants, isValidOccupancy, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { CLEAR_OFFER_CHECK_IN_CHECK_OUT, SET_OFFER_CHECK_IN_CHECK_OUT } from 'actions/actionConstants'
import ProductPaletteProvider, { ProductPalette } from 'contexts/ProductPaletteContext'
import AlwaysOnRoomOptions from '../AlwaysOn/AlwaysOnRoomOptions'
import FlashHotelPackageOptions from '../FlashHotel/FlashHotelPackageOptions'
import VideoLightbox from 'components/Luxkit/Carousel/VideoLightbox'
import LightBoxContext from 'components/Luxkit/Modal/LightBoxContext'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import GeoContext from 'contexts/geoContext'
import * as Analytics from 'analytics/analytics'
import { offerPageHotLeadEvent } from 'analytics/snowplow/events'
import { multiRoomEnabled } from 'selectors/featuresSelectors'
import MultipleRoomsBookingContent from 'components/OfferPage/MultipleRoomsBooking/MultipleRoomsBookingContent'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import HotelOfferPageStickyFooter from '../HotelOfferPageStickyFooter'
import LuxuryPlusNotificationBanner from 'loyalty/components/LuxuryPlusNotificationBanner'
import { isLuxuryPlusEnabled } from 'loyalty/selectors/featureToggle'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import OfferDurationTabs from '../Filters/DurationTabs/OfferDurationTabs'
import { ReducerMiddleware } from 'lib/redux/reducerUtils'
import { getNextFilterWithCapacityCheckInMiddlware } from '../utils'

const RelativeBlock = styled.form`
  position: relative;
`

const BarShadow = styled(Pane)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  pointer-events: none;
`

const PackageOptionsPane = styled(Pane)`
  padding: ${rem(20)} 0;
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

const StyledFlashHotelStickyHeader = styled(FlashHotelHeader)`
  position: relative;

  &.hideOnMobile.hideOnMobile {
    display: none;

    ${mediaQueryUp.tablet} {
      display: flex;
      visibility: visible;
    }
  }
`

const middlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [getNextFilterWithCapacityCheckInMiddlware]

type CartDefaults = Partial<OfferPageState>

interface Props {
  defaults: CartDefaults;
  offer: App.Offer;
  originAirports?: Array<App.Airport>;
  airports?: Array<App.Airport>;
  routerLocation: RouterLocation<unknown>;
  currency: string;
  headlessMode: boolean;
  isLuxPlusEnabled: boolean;
  isMultiRoomEnabled: boolean;
}

const fallbackRooms = [generateRoomOccupants()]

function UltraLuxContent(props: Props) {
  const {
    offer,
    originAirports,
    airports,
    defaults,
    routerLocation,
    headlessMode,
    isMultiRoomEnabled,
    isLuxPlusEnabled,
  } = props

  const {
    id,
    packages,
  } = offer

  // calendars only start fetching once the user has tried to look at dates
  // after that, try keep the calendars up to date
  const [viewedDates, setViewedDates] = useState(!!defaults.checkInDate)
  const [state, stateDispatch, stateSnapshotRef] = useOfferPageReducer({
    ...defaults,
    duration: defaults.duration ?? offer.lowestPricePackage?.duration,
    offer,
    flights: offer.bundledWithFlightsOnly,
  }, middlewares)

  const {
    airport,
    duration,
    activeFilter,
    rooms = fallbackRooms,
    package: selectedPackage,
    checkInDate,
  } = state

  const dispatch = useAppDispatch()
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousState = usePrevious<OfferPageState>(state, {})
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.RoomOccupants>>([])
  const lastFetchedAirport = useRef<App.Airport>()
  const canBookMultiRoomTypes = isMultiRoomEnabled && rooms.length > 1

  const [stickyFooterVisible, setIsStickyFooterVisible] = useState<boolean>(true)
  const onStickyFooterToggle = useCallback((visible: boolean) => {
    setIsStickyFooterVisible(visible)
  }, [])

  useSyncHotelTripItem(state, offer)

  const isoCheckIn = state?.checkInDate?.format(ISO_DATE_FORMAT)
  const isoCheckOut = state?.checkOutDate?.format(ISO_DATE_FORMAT)

  // Need to pass the context state above the tree
  useEffect(() => {
    if (state.offer) {
      dispatch({
        type: SET_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: state.offer.id,
          checkIn: isoCheckIn,
          checkOut: isoCheckOut,
          duration,
          rooms,
        },
      })
    }
    return () => {
      dispatch({
        type: CLEAR_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: state.offer.id,
        },
      })
    }
  }, [dispatch, state.offer, isoCheckIn, isoCheckOut, duration, rooms])

  useEffect(() => {
    if (routerLocation.query.roomTypeId && routerLocation.query.roomRateId) {
      const roomOption = { roomTypeId: routerLocation.query.roomTypeId, roomRateId: routerLocation.query.roomRateId }
      stateDispatch({ type: OfferPageActions.setRoomOption, roomOption })
    }
  }, [routerLocation.query.roomTypeId, routerLocation.query.roomRateId, stateDispatch])

  useMountedEffect(() => {
    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
    previousState.rooms !== rooms ||
    previousState.checkInDate !== checkInDate
  }, [state.rooms, state.checkInDate])

  const scrollToPackageOptions = useCallback(() => {
    packageOptionsAnchor.current?.scrollIntoView()
  }, [])

  const fetchCalendarData = useCallback(() => {
    if (!offer.packages.length || !isValidOccupancy(...rooms)) {
      // if we have any invalid ages or no packages, don't try fetch - it'll fail anyway
      return
    }

    if (canBookMultiRoomTypes) {
      dispatch(fetchCalendarsByOccupancy(id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: offer.packages.filter(p => p.duration === duration),
      }))
    } else {
      dispatch(fetchCalendars(id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: [selectedPackage ?? offer.lowestPricePackage],
      }))
      dispatch(fetchCalendars(id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: offer.packages.filter(p => p.duration === duration),
      }))
    }

    lastFetchedRooms.current = rooms
    lastFetchedAirport.current = airport
  }, [airport, canBookMultiRoomTypes, dispatch, duration, id, offer, rooms, selectedPackage])

  const showLightbox = useContext(LightBoxContext)

  const watchVideo = useCallback(() => {
    showLightbox(<VideoLightbox videoId={offer.vimeoVideoId} playOnMount />)
  }, [offer.vimeoVideoId, showLightbox])

  useEffect(() => {
    const hasRooms = rooms !== fallbackRooms
    const hasDate = !!checkInDate
    if (previousFilter === 'capacity' &&
        !hasRooms &&
        !activeFilter
    ) {
      scrollToPackageOptions()
      scrollAfterFilterClose.current = false
    }

    if (activeFilter) {
      scrollToPackageOptions()
    }

    if (previousFilter && !activeFilter && hasRooms && hasDate) {
      if (scrollAfterFilterClose.current) {
        if (state.room) {
          window.location.hash = state.room.id
        } else {
          scrollToPackageOptions()
        }
      }
      scrollAfterFilterClose.current = false
    }

    if (activeFilter === 'checkin' && !viewedDates) {
      setViewedDates(true)
      fetchCalendarData()
    } else {
      if (viewedDates && (lastFetchedAirport.current !== airport || (!canBookMultiRoomTypes && lastFetchedRooms.current !== rooms))) {
        // rooms/airport has changed, all loaded calendars are no longer valid as we load calendars for a specific
        // combinations of flight origin (flight prices) and rooms (availablity), so clear them
        dispatch(clearBookingCalendar())
        fetchCalendarData()
      } else if (canBookMultiRoomTypes && lastFetchedRooms.current !== rooms) {
        // if multiple room booking is available and we changed the rooms,
        // don't clear the previous data, just update for the new changed
        // rooms (is filtered inside fetchCalendarsByOccupancy action)
        fetchCalendarData()
      }
    }
  }, [activeFilter, fetchCalendarData]) // eslint-disable-line react-hooks/exhaustive-deps

  const geoState = useContext(GeoContext)
  const hotLead = useSaveHotLead({
    offerId: id,
    offerType: offer.type,
    checkInDate,
    duration,
    occupancy: rooms,
    defaultOccupancy: fallbackRooms,
  })

  useEffect(() => {
    if (hotLead?.checkIn && hotLead.checkOut) {
      Analytics.track('offer-page-hot-lead-event', offerPageHotLeadEvent(offer, hotLead, geoState))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotLead])

  useMountedEffect(() => {
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  useEffect(() => {
    // once we load packages and have dates, get our calendar data
    if (viewedDates && offer.packages) {
      fetchCalendarData()
    }
  // eslint-disable-next-line
  }, [offer.packages])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (viewedDates) {
      fetchCalendarData()
    }

    if (!activeFilter) {
      scrollToPackageOptions()
    }
  }, [duration])

  useMountedEffect(() => {
    // effectively an 'on airport change' event
    dispatch(setRecentlyUsedAirportCode(airport.code))
    dispatch(fetchOfferFlightPrice(id, airport.code))
  }, [airport])

  useEffect(() => {
    if (routerLocation.search) {
      const newSearch = removeSearchParamsFromURL(routerLocation.search)
      dispatch(replace({ ...routerLocation, search: newSearch }))
    }

    if (routerLocation.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const hasSelectedPackageWithDifferentDuration = selectedPackage && selectedPackage?.duration !== duration
  useEffect(() => {
    const hasDate = !!checkInDate
    const useDynamicCancellationPolicies = offer.property.useDynamicCancellationPolicies ?? false
    const occupanciesInvalid = rooms.some(item => item.childrenAge && item.childrenAge.some(age => age < 0))
    if (hasDate && !occupanciesInvalid && (!activeFilter || hasSelectedPackageWithDifferentDuration) && useDynamicCancellationPolicies) {
      dispatch(fetchAvailableRatesForOffer(
        offer,
        {
          occupants: rooms,
          checkIn: checkInDate.format(ISO_DATE_FORMAT),
          checkOut: checkInDate.clone().add(duration, 'days').format(ISO_DATE_FORMAT),
          flightOrigin: airport?.code,
        },
      ))
    }
  }, [duration, activeFilter, checkInDate, offer, hasSelectedPackageWithDifferentDuration, dispatch, rooms, airport])

  const occupancyNumber = useMemo(() => sumUpOccupancies(rooms), [rooms])

  const occupancyLimits = useMemo(() => {
    return packages.flatMap((p) => p.roomRate.capacities)
  }, [packages])

  const isValidRoomOccupancy = offer.bundledWithFlightsOnly || occupancyNumber < 10
  const destinationAirport = useMemo(() => airports.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])
  const showFlightInformation = destinationAirport && (originAirports.length > 1 || (originAirports.length > 0 && destinationAirport.code !== airport?.code)) && isValidRoomOccupancy && config.businessTraveller.currentAccountMode !== 'business'
  const availableForPurchase = useIsAvailableForPurchase(state)

  const hideSoldOutOnMobile = !availableForPurchase && config.LERE_EXPIRED_DEALS_V2_ENABLED

  const paletteValue: ProductPalette = isLuxPlusEnabled && !!offer.loyalty?.access ? 'loyalty' : 'ultralux'

  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)

  return (
    <ProductPaletteProvider palette={paletteValue}>
      <OfferPageStateProvider state={state} stateSnapshotRef={stateSnapshotRef} dispatch={stateDispatch}>
        <ImmersiveSaveButtonPortal>
          <BookmarkButton offer={offer} />
        </ImmersiveSaveButtonPortal>
        <OfferUpgradeModalContext>
          <RelativeBlock onSubmit={(e) => e.preventDefault()}>
            <CSSBreakpoint min="tablet">
              <StyledFlashHotelStickyHeader
                offer={offer}
                onViewPackageOptions={scrollToPackageOptions}
                className={cx({ hideOnMobile: hideSoldOutOnMobile })}
              >
                {!headlessMode && <DesktopRelatedTravelItems offer={offer} />}
                <Backdrop visible={!!activeFilter} data-testid="filter-backdrop" />
                {!hideSoldOutOnMobile && <BarShadow shadow type="clear" />}
              </StyledFlashHotelStickyHeader>
            </CSSBreakpoint>
            {isLuxPlusEnabled && !!offer.loyalty?.access &&
              <LuxuryPlusNotificationBanner>
                {offer.loyalty.access === 'earlyAccess' && 'Members get early access to this offer before the general public and more'}
                {offer.loyalty.access === 'memberOnly' && 'You get exclusive access to this member-only offer'}
              </LuxuryPlusNotificationBanner>
            }
            {availableForPurchase &&
              <Breakpoint max="mobile">
                <FlashHotelSidebarFilters
                  onViewPackageOptions={scrollToPackageOptions}
                  capacities={occupancyLimits}
                />
              </Breakpoint>
            }
            <ExpiredDealsLereRecommendations
              hideOffers={!!activeFilter}
            />
            <OfferPageGallery offer={offer} />
            <Pane>
              <OfferIntro
                onWatchVideo={watchVideo}
                onViewPackageOptions={scrollToPackageOptions}
                flightDestination={destinationAirport}
              />
            </Pane>
            <ScrollAnchor ref={packageOptionsAnchor} />
            <PackageOptionsPane type="light" id="Package-Options">
              <LayoutContainer>
                <VerticalSpacer gap={32}>
                  <VerticalSpacer gap={16}>
                    <Heading variant="heading2" format="titlecase" as="h2" align="center">
                      {config.brandConfig.travelSynonym} Options
                    </Heading>
                    {availableForPurchase && <>
                      <CSSBreakpoint max="mobile">
                        <Group direction="vertical" gap={8}>
                          <OfferCapacityWithAgesSelectBox />
                          <OfferCalendarDatePickerSelectBox />
                          {showFlightInformation && <OfferAirportSelectBox />}
                        </Group>
                      </CSSBreakpoint>
                      <CSSBreakpoint min="tablet">
                        <DesktopFilterBlock ref={desktopFiltersWrapperRef} direction="horizontal" gap={8} horizontalAlign="center" className={cn({ open: !!activeFilter })}>
                          <OfferCapacityWithAgesSelect
                            boundaryRef={desktopFiltersWrapperRef}
                            capacities={occupancyLimits}
                          />
                          <OfferCalendarDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />
                          {showFlightInformation && <OfferAirportSelect />}
                        </DesktopFilterBlock>
                      </CSSBreakpoint>
                    </>}
                  </VerticalSpacer>
                  <VerticalSpacer gap={8}>
                    <Subtitle variant="subtitle3" align="center">
                      Select duration
                    </Subtitle>
                    <OfferDurationTabs />
                  </VerticalSpacer>
                </VerticalSpacer>
              </LayoutContainer>
            </PackageOptionsPane>
            {availableForPurchase && <HotelOfferPageStickyFooter
              visible={stickyFooterVisible}
              lowestPricePackage={offer.lowestPricePackage ?? offer.packages[0]}
              onScrollTo={scrollToPackageOptions}
              offer={offer}
            />}
            {packages.length > 0 && !canBookMultiRoomTypes && (<>
              {offer.type === 'hotel' && <FlashHotelPackageOptions
                onStickyFooterToggle={onStickyFooterToggle}
                showFlightInformation={showFlightInformation}
                destinationAirport={destinationAirport}
              />}
              {offer.type === 'tactical_ao_hotel' && <AlwaysOnRoomOptions
                  showFlightInformation={showFlightInformation}
                  destinationAirport={destinationAirport}
                  onViewPackageOptions={scrollToPackageOptions}
                />}
            </>)}
            {canBookMultiRoomTypes && (
              <MultipleRoomsBookingContent />
            )}
          </RelativeBlock>
        </OfferUpgradeModalContext>
      </OfferPageStateProvider>
    </ProductPaletteProvider>
  )
}

UltraLuxContent.defaultProps = {
  originAirports: [],
  airports: [],
  onFilterClose: noop,
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  return {
    originAirports: state.geo.originAirports,
    airports: state.geo.airports,
    defaults: getInitialLeOfferPageState(state, ownProps.offer),
    routerLocation: state.router.location,
    currency: state.geo.currentCurrency,
    headlessMode: state.config.headlessMode,
    isMultiRoomEnabled: multiRoomEnabled(state, ownProps.offer),
    isLuxPlusEnabled: isLuxuryPlusEnabled(state),
  }
}

export default connect(mapStateToProps)(UltraLuxContent)
